import React, { useState } from 'react';
import { Link } from 'react-router';
import { 
    CheckCircle2, Brain, Users, Smartphone, FileText, 
    Calendar, ShieldCheck, ArrowRight, Database, 
    Activity, QrCode, Globe, Zap, HeartPulse, 
    Clock, Monitor, Wallet, Award, Search, Check, Languages
} from 'lucide-react';

const translations = {
    en: {
        notification: "🚀 New Update: Now generate automatic prescription advice in Bengali using Gemini AI!",
        nav: {
            prescription: "Prescription",
            appointments: "Serial Management",
            website: "Doctor Website",
            features: "All Features",
            login: "Login",
            trial: "Start Free Trial"
        },
        hero: {
            badge: "DrChamber — Digital Chamber for Doctors",
            title: "The Smart System That Changes Doctor’s Life",
            subtitle: "From prescriptions to serial management—everything will be done in a snap. With DrChamber, your chamber will be digital, and your life will be stress-free.",
            cta1: "Join as Doctor",
            cta2: "View Live Demo"
        },
        section1: {
            title: "Feature One: The Smart Prescription",
            heading: "Let the Pen Rest, Free Your Mind.",
            desc: "Writing prescriptions is now as easy as water. With drug dropdowns, auto-complete SIGs, and ICD-11 diagnoses, you can create a professional prescription in just 20 seconds.",
            features: ["ICD-11 Diagnosis Codes", "Automatic Follow-up Reminders", "Patient Old History Tracking", "Bengali & English Support"],
            card1: { title: "AI Medicine Suggestion", desc: "Gemini AI gives automatic medicine suggestions for patients." },
            card2: { title: "AI Advice", desc: "Automatically generate lifestyle advice in English using Gemini AI for patients." },
            card3: { title: "AI Dose Suggestion", desc: "Gemini AI gives automatic dose suggestions for patients." },
            card4: { title: "AI Drug Interaction Check", desc: "Gemini AI gives automatic drug interaction checks for patients." }
        },
        section2: {
            title: "Feature Two: Advanced Scheduling",
            heading: "Say Goodbye to Serial Chaos.",
            desc: "Managing crowds in front of your chamber is now easy for you or your receptionist. Control serials with smart queue management.",
            card1: { title: "Online Booking", desc: "Patients can book serials directly from your website. You just confirm appointments.", badge: "Real-time Slot Updates" },
            card2: { title: "Offline Booking", desc: "Easily register serials received through phone calls or walk-in patients and keep everything organized in the same online serial list.", badge: "Create a Smooth Workflow" },
            card3: { title: "Staff Dashboard", desc: "Your receptionist or assistant can login separately to enter patient names, ages, and vitals.", badge: "Role-based Access Control" },
            card4: { title: "Global Reception", desc: "Manage serials from multiple chambers in one central dashboard. For multi-chamber management.(comming soon)", badge: "Multi-chamber Management" },
        },
        section3: {
            title: "Feature Three: Personal Portfolio",
            heading: "Your Own Professional Website.",
            desc: "In the digital age, you need a unique identity. DrChamber gives you a personalized doctor profile page to take your practice to new heights.",
            feature1: { title: "Google Search Visibility", desc: "When patients search online, they'll find your chamber location and hours." },
            feature2: { title: "Custom Profile Slug", desc: "DrChamber.com/dr-rahim—a unique profile URL with your preferred name." }
        },
        section4: {
            title: "The All-in-One Powerhouse",
            subtitle: "Everything a modern smart chamber needs is here.",
            features: [
                { title: "AI-Powered Prescriptions", desc: "Generate professional prescriptions in 20 seconds with Gemini AI assistance and auto-complete SIGs.", icon: "brain" },
                { title: "10,000+ Drug Database", desc: "Access all popular brands and generic drugs in Bangladesh with automatic dosage suggestions.", icon: "database" },
                { title: "Smart Serial Management", desc: "Online & offline booking with real-time queue management and automatic synchronization.", icon: "calendar" },
                { title: "Patient QR History", desc: "Patients scan QR code to access their complete medical history, reports, and prescriptions instantly.", icon: "qrcode" },
                { title: "Staff Dashboard", desc: "Role-based access control for receptionists, assistants, and doctors with activity tracking.", icon: "users" },
                { title: "Multi-Chamber Control", desc: "Manage multiple clinic locations from a single central dashboard with unified reporting.", icon: "monitor" },
                { title: "Data Security", desc: "Military-grade 256-bit encryption for all patient data with HIPAA-compliant backup systems.", icon: "shield" },
                { title: "Payment Integration", desc: "Accept payments via bKash, Nagad, Visa, and Mastercard with automatic reconciliation.", icon: "wallet" },
                { title: "Referral Commission", desc: "Earn attractive commissions by referring doctors to DrChamber with automated payout system.", icon: "zap" },
                { title: "Health Analytics", desc: "Real-time dashboards showing daily visits, revenue trends, and comprehensive business insights.", icon: "activity" },
                { title: "Dual PDF Templates", desc: "Print official prescriptions and patient-friendly Bengali guides separately for better compliance.", icon: "filetext" },
                { title: "Doctor Profile Website", desc: "Get your own professional website (DrChamber.com/dr-yourname) with Google SEO optimization.", icon: "globe" },
                { title: "Mobile Ready", desc: "Seamless experience on smartphones, tablets, and desktops with offline functionality.", icon: "smartphone" },
                { title: "Custom Drugs", desc: "Add your own drug formulas, clinic-specific medications, and custom treatment protocols.", icon: "plus" },
                { title: "ICD-11 Diagnosis", desc: "Complete ICD-11 diagnosis code library with auto-search and follow-up reminders.", icon: "check" },
                { title: "Bilingual Support", desc: "Full support for Bengali and English with automatic language switching based on user preference.", icon: "languages" }
            ]
        },
        comparison: {
            without: {
                title: "Life Without DrChamber",
                items: [
                    "Hand pain and fatigue from writing prescriptions.",
                    "Chaos and fights over serials in front of your chamber.",
                    "Wasting hours searching for old patient files.",
                    "No professional digital identity outside your chamber."
                ]
            },
            with: {
                title: "Best Life With DrChamber",
                items: [
                    "Accurate and beautiful digital prescription in just 1 minute.",
                    "Staff collects vitals beforehand so you focus on treatment.",
                    "One QR scan and the patient's entire history appears instantly.",
                    "Reach thousands of new patients through your own website."
                ]
            }
        },
        howItWorks: {
            title: "How It Works",
            subtitle: "Get your chamber digital in 3 simple steps",
            steps: [
                {
                    number: "01",
                    title: "Sign Up (2 Minutes)",
                    desc: "Create your account with your clinic details and get instant access to all features."
                },
                {
                    number: "02",
                    title: "Set Up Your Profile (5 Minutes)",
                    desc: "Add your chamber details, working hours, staff members, and your own professional website."
                },
                {
                    number: "03",
                    title: "Start Prescribing (Immediately)",
                    desc: "Create your first digital prescription and start managing patients with DrChamber."
                }
            ]
        },
        testimonials: {
            title: "Trusted by 500+ Doctors",
            subtitle: "See how doctors across Bangladesh are transforming their practice",
            testimonials: [
                {
                    name: "Dr. Md. Rahim",
                    title: "Specialist Physician, Dhaka",
                    text: "DrChamber has ended my hand pain and time wastage. Now I can save an extra 30 minutes every day.",
                    rating: 5,
                    image: "👨‍⚕️"
                },
                {
                    name: "Dr. Fatima Akter",
                    title: "General Practitioner, Chittagong",
                    text: "আমার রোগীরা এখন QR কোড স্ক্যান করে তাদের প্রেসক্রিপশন ইতিহাস পেতে পারে। অসাধারণ সুবিধা!",
                    rating: 5,
                    image: "👩‍⚕️"
                },
                {
                    name: "Dr. Karim Ahmed",
                    title: "Cardiologist, Sylhet",
                    text: "The referral system helped me earn extra ৳50,000 last month. Best investment for my clinic!",
                    rating: 5,
                    image: "👨‍⚕️"
                },
                {
                    name: "Dr. Sheila Roy",
                    title: "Pediatrician, Khulna",
                    text: "আমার রিসেপশনিস্ট এখন সিরিয়াল ম্যানেজমেন্ট করে। রোগীদের ঝগড়া সম্পূর্ণ বন্ধ হয়েছে।",
                    rating: 5,
                    image: "👩‍⚕️"
                }
            ]
        },
        faq: {
            title: "Frequently Asked Questions",
            subtitle: "Get answers to common questions about DrChamber",
            questions: [
                {
                    q: "Do I need any technical knowledge to use DrChamber?",
                    a: "No, DrChamber is designed to be user-friendly. If you can use WhatsApp, you can use DrChamber. We also provide free training and 24/7 support."
                },
                {
                    q: "Is my patient data secure?",
                    a: "Yes, we use military-grade 256-bit AES encryption, automated daily backups, and comply with HIPAA standards. Your data is safer than any physical filing system."
                },
                {
                    q: "Can I use DrChamber for multiple chambers?",
                    a: "Absolutely! DrChamber's Global Reception feature allows you to manage unlimited chambers from a single dashboard."
                },
                {
                    q: "What if I want to cancel my subscription?",
                    a: "You can cancel anytime without any penalty. We'll export all your patient data in a standard format so you can migrate to any other system."
                },
                {
                    q: "Does DrChamber work offline?",
                    a: "Yes! You can take serials, create prescriptions, and store data offline. Everything syncs automatically when you're back online."
                },
                {
                    q: "How long is the free trial?",
                    a: "7 days with full access to all premium features. No credit card required, and you can cancel anytime."
                }
            ]
        },
        pricing: {
            title: "Simple & Affordable Plans",
            free: {
                title: "Start Free",
                price: "Free",
                subtitle: "7 days full access",
                badge: "No Card Required",
                features: [
                    "Full access to all features",
                    "7-day trial period",
                    "No credit card needed",
                    "Cancel anytime"
                ],
                cta: "Start Free Trial"
            },
            monthly: {
                title: "Monthly Pro",
                price: "৳ 1,000",
                subtitle: "Pay every month",
                features: [
                    "Unlimited Prescriptions",
                    "Add all staff roles",
                    "AI Advice Generator",
                    "QR Smart History"
                ],
                cta: "Get Started"
            },
            yearly: {
                title: "Yearly Pro",
                price: "৳ 9,900",
                subtitle: "Only ৳ 9,900 instead of ৳ 12,000!",
                badge: "Save 20%",
                features: [
                    "All premium features included",
                    "Personal website URL",
                    "Priority customer support",
                    "Dedicated account manager"
                ],
                cta: "Save Now"
            }
        },
        cta: {
            title: "It takes only 2 minutes to start your smart practice.",
            subtitle: "Join today and use all premium features free for 7 days. No credit card required.",
            button: "Start Free Trial"
        },
        footer: {
            desc: "Smart clinic management software designed for doctors in Bangladesh.",
            sections: {
                chamber: "Chamber Management",
                patients: "For Patients",
                partnership: "Partnership"
            },
            links: {
                staffPanel: "Staff Panel",
                receptionLogin: "Reception Login",
                superAdmin: "Super Admin",
                findDoctors: "Find Doctors",
                downloadRx: "Download Prescription",
                newAccount: "New Account",
                partnerProgram: "Partner Program",
                affiliate: "Affiliate Portal",
                joinReferrer: "Join as Referrer"
            },
            copyright: "© {year} THEWEBPAL. Built for Bangladesh.",
            status: "● Server Online"
        }
    },
    bn: {
        notification: "🚀 নতুন আপডেট: এখন AI দিয়ে বাংলায় অটোমেটিক প্রেসক্রিপশন অ্যাডভাইস জেনারেট করুন!",
        nav: {
            prescription: "প্রেসক্রিপশন",
            appointments: "সিরিয়াল ম্যানেজমেন্ট",
            website: "ডক্টর ওয়েবসাইট",
            features: "সব ফিচার",
            login: "লগইন",
            trial: "ফ্রি ট্রায়াল শুরু করুন"
        },
        hero: {
            badge: "DrChamber — ডাক্তারের ডিজিটাল চেম্বার",
            title: "ডাক্তারের জীবন বদলে দেওয়ার স্মার্ট সিস্টেম",
            subtitle: "প্রেসক্রিপশন লেখা থেকে সিরিয়াল ম্যানেজমেন্ট—সবই হবে চোখের পলকে। DrChamber থাকলে আপনার চেম্বার হবে ডিজিটাল, আর জীবন হবে স্ট্রেস-ফ্রি।",
            cta1: "ডক্টর হিসেবে জয়েন করুন",
            cta2: "লাইভ ডেমো দেখুন"
        },
        section1: {
            title: "Feature One: The Smart Prescription",
            heading: "চিকিৎসায় মন দিন, লেখার দায় নিক সফটওয়্যার।",
            desc: "প্রেসক্রিপশন লেখা এখন হবে পানির মতো সহজ। ড্রাগ ড্রপডাউন, অটো-কমপ্লিট SIG এবং ICD-11 ডায়াগনোসিস এর মাধ্যমে আপনি মাত্র 20 সেকেন্ডে একটি প্রফেশনাল প্রেসক্রিপশন তৈরি করতে পারবেন।",
            features: ["ICD-11 ডায়াগনোসিস কোড", "অটোমেটিক ফলো-আপ রিমাইন্ডার", "পেশেন্ট ওল্ড হিস্ট্রি ট্র্যাকিং", "বাংলা ও ইংরেজি সাপোর্ট"],
            card1: { title: "AI Medicine Suggestion", desc: "Gemini AI দিয়ে রোগীর জন্য অটোমেটিক ঔষধ পরামর্শ পাওয়া যাবে।" },
            card2: { title: "AI Advice", desc: "Gemini AI দিয়ে রোগীর জন্য বাংলায় জীবনযাত্রার উপদেশ তৈরি হয় অটোমেটিক।" },
            card3: { title: "AI Dose Suggestion", desc: "Gemini AI দিয়ে রোগীর জন্য অটোমেটিক ডোজ পরামর্শ পাওয়া যাবে।" },
            card4: { title: "AI Drug Interaction Check", desc: "Gemini AI দিয়ে রোগীর জন্য অটোমেটিক ঔষধের ইন্টারঅ্যাকশন চেক হয়।" }
        },
        section2: { 
            title: "Feature Two: Advanced Scheduling", 
            heading: "সিরিয়াল ব্যবস্থার ঝামেলাকে বিদায় দিন।", 
            desc: "চেম্বারের সামনে রোগীদের ভিড় সামলানো এখন আপনার বা আপনার রিসেপশনিস্টের জন্য সহজ। স্মার্ট কিউ (Queue) ম্যানেজমেন্ট এর মাধ্যমে সিরিয়াল নিয়ন্ত্রণ করুন।",
            card1: { title: "অনলাইন বুকিং", desc: "আপনার ওয়েবসাইট থেকেই রোগীরা সরাসরি সিরিয়াল নিতে পারবে। আপনি শুধু অ্যাপয়েন্টমেন্ট কনফার্ম করবেন।", badge: "রিয়েল-টাইম স্লট আপডেট" },
            card2: { title: "অফলাইন বুকিং", desc: "ফোন কল বা সরাসরি আসা রোগীদের সিরিয়াল সহজেই রেজিস্ট্রেশন করুন এবং সবকিছু একই অনলাইন সিরিয়াল লিস্টে সুশৃঙ্খলভাবে সংরক্ষণ করুন।", badge: "সহজ, দ্রুত ও ঝামেলামুক্ত" },
            card3: { title: "স্টাফ ড্যাশবোর্ড", desc: "আপনার রিসেপশনিস্ট বা অ্যাসিস্ট্যান্ট আলাদা লগইন করে রোগীদের নাম, বয়স এবং ভাইটালস এন্ট্রি করতে পারবে।", badge: "রোল-বেজড অ্যাক্সেস কন্ট্রোল" },
            card4: { title: "গ্লোবাল রিসেপশন", desc: "একাধিক চেম্বারের সিরিয়াল একটি মাত্র সেন্ট্রাল ড্যাশবোর্ড থেকে ম্যানেজ করার আধুনিক সুবিধা।", badge: "মাল্টি-চেম্বার ম্যানেজমেন্ট" },
            
        },
        section3: {
            title: "Feature Three: Personal Portfolio",
            heading: "আপনার নিজের প্রফেশনাল ওয়েবসাইট।",
            desc: "ডিজিটাল যুগে আপনার একটি নিজস্ব পরিচয় প্রয়োজন। DrChamber আপনাকে দিচ্ছে একটি পার্সোনালাইজড ডক্টর প্রোফাইল পেজ, যা আপনার প্র্যাকটিসকে নিয়ে যাবে অন্য উচ্চতায়।",
            feature1: { title: "Google Search visibility", desc: "রোগীরা ইন্টারনেটে সার্চ করলেই আপনার চেম্বারের লোকেশন ও সময় জানতে পারবে।" },
            feature2: { title: "Custom Profile Slug", desc: "DrChamber.com/dr-rahim—আপনার পছন্দের নামে ইউনিক প্রোফাইল ইউআরএল।" }
        },
        section4: {
            title: "The All-in-One Powerhouse",
            subtitle: "একটি আধুনিক স্মার্ট চেম্বারের যা যা প্রয়োজন, তার সবই এখানে বিদ্যমান।",
            features: [
                { title: "AI-চালিত প্রেসক্রিপশন", desc: "Gemini AI সহায়তায় 20 সেকেন্ডে পেশাদার প্রেসক্রিপশন তৈরি করুন এবং স্বয়ংক্রিয় SIG পূর্ণতা।", icon: "brain" },
                { title: "১০,০০০+ ওষুধ ডাটাবেস", desc: "বাংলাদেশের সকল জনপ্রিয় ব্র্যান্ড এবং জেনেরিক ওষুধের সাথে স্বয়ংক্রিয় ডোজেজ পরামর্শ।", icon: "database" },
                { title: "স্মার্ট সিরিয়াল ম্যানেজমেন্ট", desc: "অনলাইন ও অফলাইন বুকিং রিয়েল-টাইম কিউ ম্যানেজমেন্ট এবং স্বয়ংক্রিয় সিঙ্ক্রোনাইজেশন সহ।", icon: "calendar" },
                { title: "রোগী QR হিস্ট্রি", desc: "রোগীরা QR কোড স্ক্যান করে তাদের সম্পূর্ণ মেডিকেল হিস্ট্রি এবং রিপোর্ট তাৎক্ষণিকভাবে দেখতে পারে।", icon: "qrcode" },
                { title: "স্টাফ ড্যাশবোর্ড", desc: "রিসেপশনিস্ট, সহায়ক এবং ডাক্তারদের জন্য ভূমিকা-ভিত্তিক অ্যাক্সেস এবং কার্যকলাপ ট্র্যাকিং।", icon: "users" },
                { title: "মাল্টি-চেম্বার নিয়ন্ত্রণ", desc: "একটি কেন্দ্রীয় ড্যাশবোর্ড থেকে একাধিক ক্লিনিক অবস্থান পরিচালনা করুন একীভূত রিপোর্টিং সহ।", icon: "monitor" },
                { title: "ডেটা সুরক্ষা", desc: "সমস্ত রোগী ডেটার জন্য সামরিক-গ্রেড ২৫৬-বিট এনক্রিপশন HIPAA-সম্মত ব্যাকআপ সিস্টেম সহ।", icon: "shield" },
                { title: "পেমেন্ট ইন্টিগ্রেশন", desc: "bKash, Nagad, Visa এবং Mastercard এর মাধ্যমে পেমেন্ট গ্রহণ করুন স্বয়ংক্রিয় সামঞ্জস্য সহ।", icon: "wallet" },
                { title: "রেফারেল কমিশন", desc: "ডাক্তারদের DrChamber-তে রেফার করে আকর্ষণীয় কমিশন অর্জন করুন স্বয়ংক্রিয় পেআউট সহ।", icon: "zap" },
                { title: "স্বাস্থ্য বিশ্লেষণ", desc: "দৈনিক ভিজিট, রাজস্ব প্রবণতা এবং ব্যবসায়িক অন্তর্দৃষ্টি দেখায় রিয়েল-টাইম ড্যাশবোর্ড।", icon: "activity" },
                { title: "দ্বৈত PDF টেমপ্লেট", desc: "অফিসিয়াল প্রেসক্রিপশন এবং রোগী-বান্ধব বাংলা গাইড আলাদাভাবে প্রিন্ট করার সুবিধা।", icon: "filetext" },
                { title: "ডাক্তার প্রোফাইল ওয়েবসাইট", desc: "আপনার নিজস্ব পেশাদার ওয়েবসাইট (DrChamber.com/dr-yourname) Google SEO অপ্টিমাইজেশন সহ।", icon: "globe" },
                { title: "মোবাইল প্রস্তুত", desc: "স্মার্টফোন, ট্যাবলেট এবং ডেস্কটপে নিরবচ্ছিন্ন অভিজ্ঞতা অফলাইন কার্যকারিতা সহ।", icon: "smartphone" },
                { title: "কাস্টম ওষুধ", desc: "আপনার নিজস্ব ওষুধ ফর্মুলা, ক্লিনিক-নির্দিষ্ট ওষুধ এবং কাস্টম চিকিৎসা প্রোটোকল যোগ করুন।", icon: "plus" },
                { title: "ICD-11 ডায়াগনোসিস", desc: "সম্পূর্ণ ICD-11 ডায়াগনোসিস কোড লাইব্রেরি স্বয়ংক্রিয় অনুসন্ধান এবং ফলো-আপ রিমাইন্ডার সহ।", icon: "check" },
                { title: "দ্বিভাষিক সমর্থন", desc: "বাংলা এবং ইংরেজির সম্পূর্ণ সমর্থন ব্যবহারকারীর পছন্দ অনুযায়ী স্বয়ংক্রিয় ভাষা স্যুইচিং সহ।", icon: "languages" }
            ]
        },
        comparison: {
            without: {
                title: "Life Without DrChamber",
                items: [
                    "হাতে প্রেসক্রিপশন লিখতে গিয়ে হাতের ব্যথা ও ক্লান্তিতে ভোগেন।",
                    "সিরিয়াল নিয়ে চেম্বারে রোগীদের ঝগড়া ও বিশৃঙ্খলা।",
                    "রোগীর পুরনো ফাইল খুঁজতে গিয়ে ঘণ্টার পর ঘণ্টা সময় অপচয়।",
                    "চেম্বারের বাইরে আপনার কোনো প্রফেশনাল ডিজিটাল পরিচয় নেই।"
                ]
            },
            with: {
                title: "Best Life With DrChamber",
                items: [
                    "মাত্র 20 সেকেন্ডে নির্ভুল ও সুন্দর ডিজিটাল প্রেসক্রিপশন।",
                    "স্টাফরা আগেই ভাইটালস নিয়ে রাখায় আপনি শুধু মূল চিকিৎসায় ফোকাস করতে পারেন।",
                    "কিউআর কোড স্ক্যানে এক সেকেন্ডে রোগীর পুরো লাইফ হিস্ট্রি আপনার সামনে।",
                    "নিজস্ব ওয়েবসাইটের মাধ্যমে হাজার হাজার নতুন রোগীর কাছে পৌঁছান।"
                ]
            }
        },
        howItWorks: {
            title: "কীভাবে কাজ করে",
            subtitle: "আপনার চেম্বার ডিজিটাল করুন মাত্র ৩টি সহজ ধাপে",
            steps: [
                {
                    number: "০১",
                    title: "সাইন আপ করুন (২ মিনিট)",
                    desc: "আপনার চেম্বার বিবরণ সহ অ্যাকাউন্ট তৈরি করুন এবং সমস্ত ফিচারে তাৎক্ষণিক অ্যাক্সেস পান।"
                },
                {
                    number: "০२",
                    title: "প্রোফাইল সেটআপ করুন (৫ মিনিট)",
                    desc: "আপনার চেম্বারের বিবরণ, কাজের সময়, কর্মীদের তথ্য এবং প্রফেশনাল ওয়েবসাইট যোগ করুন।"
                },
                {
                    number: "०३",
                    title: "প্রেসক্রিপশন শুরু করুন (তাৎক্ষণিক)",
                    desc: "আপনার প্রথম ডিজিটাল প্রেসক্রিপশন তৈরি করুন এবং DrChamber দিয়ে রোগীদের পরিচালনা শুরু করুন।"
                }
            ]
        },
        testimonials: {
            title: "৫০০+ ডাক্তার আমাদের বিশ্বাস করেন",
            subtitle: "দেখুন কীভাবে বাংলাদেশের ডাক্তাররা তাদের প্র্যাকটিস পরিবর্তন করছেন",
            testimonials: [
                {
                    name: "ডাক্তার ইব্রাহিম কামাল",
                    title: "বিশেষজ্ঞ চিকিৎসক, ঢাকা",
                    text: "DrChamber আমার হাতের ব্যথা এবং সময়ের অপচয় শেষ করেছে। এখন প্রতিদিন ৩০ মিনিট অতিরিক্ত বাঁচাতে পারি।",
                    rating: 5,
                    image: "👨‍⚕️"
                },
                {
                    name: "ডাক্তার ফাতেমা আকতার",
                    title: "সাধারণ চিকিৎসক, চট্টগ্রাম",
                    text: "আমার রোগীরা এখন QR কোড স্ক্যান করে তাদের প্রেসক্রিপশন ইতিহাস পেতে পারে। অসাধারণ সুবিধা!",
                    rating: 5,
                    image: "👩‍⚕️"
                },
                {
                    name: "ডাক্তার করিম আহমদ",
                    title: "হৃদরোগ বিশেষজ্ঞ, সিলেট",
                    text: "রেফারেল সিস্টেম আমাকে গত মাসে অতিরিক্ত ৳৫০,০০০ আয় করতে সাহায্য করেছে। আমার চেম্বারের জন্য সেরা বিনিয়োগ!",
                    rating: 5,
                    image: "👨‍⚕️"
                },
                {
                    name: "ডাক্তার শীলা রয়",
                    title: "শিশুরোগ বিশেষজ্ঞ, খুলনা",
                    text: "আমার রিসেপশনিস্ট এখন সিরিয়াল ম্যানেজমেন্ট করে। রোগীদের ঝগড়া সম্পূর্ণ বন্ধ হয়েছে।",
                    rating: 5,
                    image: "👩‍⚕️"
                }
            ]
        },
        faq: {
            title: "সাধারণ প্রশ্নোত্তর",
            subtitle: "DrChamber সম্পর্কে সাধারণ প্রশ্নের উত্তর জানুন",
            questions: [
                {
                    q: "DrChamber ব্যবহার করতে আমার প্রযুক্তিগত জ্ঞানের প্রয়োজন আছে?",
                    a: "না, DrChamber ব্যবহারকারী-বান্ধব করে তৈরি করা হয়েছে। আপনি যদি WhatsApp ব্যবহার করতে পারেন, তাহলে আপনি DrChamber ব্যবহার করতে পারবেন। আমরা বিনামূল্যে প্রশিক্ষণ এবং ২৪/৭ সহায়তা প্রদান করি।"
                },
                {
                    q: "আমার রোগীর তথ্য নিরাপদ থাকবে?",
                    a: "হ্যাঁ, আমরা সামরিক-গ্রেড ২৫৬-বিট AES এনক্রিপশন, স্বয়ংক্রিয় দৈনিক ব্যাকআপ এবং HIPAA মান মেনে চলি। আপনার ডেটা যেকোনো ফিজিক্যাল ফাইলিং সিস্টেমের চেয়ে নিরাপদ।"
                },
                {
                    q: "আমি একাধিক চেম্বারের জন্য DrChamber ব্যবহার করতে পারি?",
                    a: "হ্যাঁ, DrChamberর গ্লোবাল রিসেপশন ফিচার আপনাকে একটি ড্যাশবোর্ড থেকে সীমাহীন চেম্বার পরিচালনা করতে দেয়।"
                },
                {
                    q: "আমি যদি আমার সাবস্ক্রিপশন বাতিল করতে চাই?",
                    a: "আপনি যেকোনো সময় কোনো পেনাল্টি ছাড়াই বাতিল করতে পারেন। আমরা আপনার সমস্ত রোগীর তথ্য একটি স্ট্যান্ডার্ড ফরম্যাটে এক্সপোর্ট করে দেব।"
                },
                {
                    q: "DrChamber অফলাইনে কাজ করে?",
                    a: "হ্যাঁ! আপনি অফলাইনে সিরিয়াল নিতে, প্রেসক্রিপশন তৈরি করতে এবং ডেটা সংরক্ষণ করতে পারেন। ইন্টারনেট ফিরে এলে সবকিছু স্বয়ংক্রিয়ভাবে সিঙ্ক হয়।"
                },
                {
                    q: "ফ্রি ট্রায়াল কত দিনের জন্য?",
                    a: "সম্পূর্ণ প্রিমিয়াম ফিচার অ্যাক্সেস সহ ৭ দিনের ট্রায়াল। ক্রেডিট কার্ডের প্রয়োজন নেই এবং যেকোনো সময় বাতিল করতে পারেন।"
                }
            ]
        },
        pricing: {
            title: "সহজ ও সাশ্রয়ী প্ল্যান",
            free: {
                title: "ফ্রি শুরু করুন",
                price: "বিনামূল্যে",
                subtitle: "৭ দিন সম্পূর্ণ অ্যাক্সেস",
                badge: "কোনো কার্ডের প্রয়োজন নেই",
                features: [
                    "সকল ফিচারে সম্পূর্ণ অ্যাক্সেস",
                    "৭ দিনের ট্রায়াল পিরিয়ড",
                    "ক্রেডিট কার্ড ছাড়াই শুরু করুন",
                    "যেকোনো সময় বাতিল করুন"
                ],
                cta: "ফ্রি ট্রায়াল শুরু করুন"
            },
            monthly: {
                title: "Monthly Pro",
                price: "৳ ১,০০০",
                subtitle: "প্রতি মাসে পেমেন্ট করুন",
                features: [
                    "আনলিমিটেড প্রেসক্রিপশন",
                    "সব স্টাফ রোল অ্যাড করার সুবিধা",
                    "AI অ্যাডভাইস জেনারেটর",
                    "QR স্মার্ট হিস্ট্রি"
                ],
                cta: "শুরু করুন"
            },
            yearly: {
                title: "Yearly Pro",
                price: "৳ ৯,৯০০",
                subtitle: "৳ ১২,০০০  এর বদলে মাত্র ৯,৯০০  টাকা!",
                badge: "২০% সাশ্রয় করুন",
                features: [
                    "সব প্রিমিয়াম ফিচার অন্তর্ভুক্ত",
                    "পার্সোনাল ওয়েবসাইট ইউআরএল",
                    "প্রায়োরিটি কাস্টমার সাপোর্ট",
                    "ডেডিকেটেড অ্যাকাউন্ট ম্যানেজার"
                ],
                cta: "এখনই সেভ করুন"
            }
        },
        cta: {
            title: "আপনার স্মার্ট প্র্যাকটিস শুরু করতে মাত্র ২ মিনিট সময় লাগবে।",
            subtitle: "আজই জয়েন করুন এবং ৭ দিনের জন্য সকল প্রিমিয়াম ফিচার ফ্রিতে ব্যবহার করুন। কোনো ক্রেডিট কার্ডের প্রয়োজন নেই।",
            button: "ফ্রি ট্রায়াল শুরু করুন"
        },
        footer: {
            desc: "বাংলাদেশের ডাক্তারদের জন্য তৈরি স্মার্ট চেম্বার ম্যানেজমেন্ট সফটওয়্যার।",
            sections: {
                chamber: "চেম্বার ম্যানেজমেন্ট",
                patients: "রোগীদের জন্য",
                partnership: "পার্টনারশিপ"
            },
            links: {
                staffPanel: "স্টাফ প্যানেল",
                receptionLogin: "রিসেপশন লগইন",
                superAdmin: "সুপার অ্যাডমিন",
                findDoctors: "ডাক্তার খুঁজুন",
                downloadRx: "প্রেসক্রিপশন ডাউনলোড",
                newAccount: "নতুন অ্যাকাউন্ট",
                partnerProgram: "পার্টনার প্রোগ্রাম",
                affiliate: "অ্যাফিলিয়েট পোর্টাল",
                joinReferrer: "রেফারার হিসেবে জয়েন"
            },
            copyright: "© {year} THEWEBPAL Built for Bangladesh.",
            status: "● Server Online"
        }
    }
};

function Home() {
    const [language, setLanguage] = useState('bn');
    const t = translations[language];

    return (
        <div className="min-h-screen bg-white flex flex-col font-sans text-gray-900">
            
            {/* --- TOP NOTIFICATION BAR --- */}
            <div className="bg-indigo-950 text-white py-2 text-center text-sm font-medium">
                {t.notification}
            </div>

            {/* --- NAVIGATION --- */}
            <nav className="sticky top-0 w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-linear-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                            <HeartPulse className="text-white w-6 h-6" />
                        </div>
                        <span className="font-black text-2xl tracking-tighter bg-clip-text text-transparent bg-linear-to-r from-indigo-900 to-indigo-600">DrChamber</span>
                    </div>
                    
                    <div className="hidden lg:flex items-center gap-8 text-[15px] font-bold text-gray-600">
                        <a href="#prescription" className="hover:text-indigo-600 transition">{t.nav.prescription}</a>
                        <a href="#appointment" className="hover:text-indigo-600 transition">{t.nav.appointments}</a>
                        <a href="#website" className="hover:text-indigo-600 transition">{t.nav.website}</a>
                        <a href="#features" className="hover:text-indigo-600 transition">{t.nav.features}</a>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setLanguage(language === 'bn' ? 'en' : 'bn')}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-sm font-bold text-gray-700"
                            title="Toggle Language"
                        >
                            <Languages size={18} />
                            {language === 'bn' ? 'EN' : 'বাংলা'}
                        </button>
                        <Link to="/login" className="text-sm font-bold text-gray-700 px-4 py-2 hover:bg-gray-100 rounded-lg transition">{t.nav.login}</Link>
                        <Link to="/register" className="px-6 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition shadow-xl shadow-indigo-200">
                            {t.nav.trial}
                        </Link>
                    </div>
                </div>
            </nav>

            {/* --- HERO SECTION: THE "BEST LIFE" PROMISE --- */}
            <header className="relative pt-20 pb-20 lg:pt-32 lg:pb-40 overflow-hidden">
                <div className="absolute inset-0 z-0 opacity-40 pointer-events-none bg-[radial-gradient(#4f46e5_1.5px,transparent_1.5px)] bg-size-[32px_32px]"></div>
                
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="flex flex-col lg:flex-row items-center gap-16">
                        <div className="flex-1 text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-100 text-green-700 text-xs font-bold uppercase tracking-widest mb-6">
                                <Award className="w-4 h-4" /> {t.hero.badge}
                            </div>
                            <h1 className="text-5xl lg:text-7xl font-black text-gray-900 leading-[1.1] mb-8">
                                {t.hero.title}
                            </h1>
                            <p className="text-xl lg:text-2xl text-gray-600 mb-10 leading-relaxed font-medium">
                                {t.hero.subtitle}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <Link to="/register" className="px-10 py-5 bg-indigo-600 text-white text-xl font-bold rounded-2xl shadow-2xl shadow-indigo-300 hover:bg-indigo-700 transition transform hover:-translate-y-1">
                                    {t.hero.cta1}
                                </Link>
                                <Link to="/find-doctors" className="px-10 py-5 bg-white border-2 border-gray-200 text-gray-700 text-xl font-bold rounded-2xl hover:border-indigo-600 transition">
                                    {t.hero.cta2}
                                </Link>
                            </div>
                        </div>
                        <div className="flex-1 relative">
                            {/* Visual Mockup Placeholder */}
                            <div className="bg-linear-to-br from-indigo-100 to-blue-50 p-4 rounded-[2.5rem] shadow-3xl border border-white">
                                <div className="bg-white rounded-4xl p-6 shadow-inner">
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="h-4 w-24 bg-gray-100 rounded"></div>
                                        <div className="h-8 w-8 bg-indigo-100 rounded-full"></div>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="h-10 w-full bg-indigo-50 rounded-xl flex items-center px-4 text-indigo-600 font-bold text-sm">Patient: Md. Rahim Ali (Serial #05)</div>
                                        <div className="h-52 w-full bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 overflow-hidden">
                                            <img
                                                src="/images/ai-prescription.png"
                                                alt="AI Prescription Generator"
                                                className="w-full animate-scroll-once"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="h-16 bg-green-50 rounded-xl p-3">
                                                <div className="text-[10px] text-green-600 font-bold uppercase">BP</div>
                                                <div className="text-xl font-black">120/80</div>
                                            </div>
                                            <div className="h-16 bg-blue-50 rounded-xl p-3">
                                                <div className="text-[10px] text-blue-600 font-bold uppercase">Pulse</div>
                                                <div className="text-xl font-black">72 bpm</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Floating Stats */}
                            <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-3">
                                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600"><Clock /></div>
                                <div>
                                    <div className="text-xs text-gray-500 font-bold">Time Saved</div>
                                    <div className="text-lg font-black">2+ Hours Daily</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* --- SECTION 1: SMART PRESCRIPTION (THE CORE) --- */}
            <section id="prescription" className="py-24 bg-white border-t border-gray-50">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col lg:flex-row items-center gap-16">
                        <div className="flex-1 order-2 lg:order-1">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                                    <Brain className="text-indigo-600 mb-4" size={32} />
                                    <h4 className="font-bold text-lg mb-2 text-gray-900">{t.section1.card1.title}</h4>
                                    <p className="text-sm text-gray-600 leading-relaxed font-bold">{t.section1.card1.desc}</p>
                                </div>
                                <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                                    <Database className="text-blue-600 mb-4" size={32} />
                                    <h4 className="font-bold text-lg mb-2 text-gray-900">{t.section1.card2.title}</h4>
                                    <p className="text-sm text-gray-600 leading-relaxed font-bold">{t.section1.card2.desc}</p>
                                </div>
                                <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                                    <FileText className="text-green-600 mb-4" size={32} />
                                    <h4 className="font-bold text-lg mb-2 text-gray-900">{t.section1.card3.title}</h4>
                                    <p className="text-sm text-gray-600 leading-relaxed font-bold">{t.section1.card3.desc}</p>
                                </div>
                                <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                                    <QrCode className="text-pink-600 mb-4" size={32} />
                                    <h4 className="font-bold text-lg mb-2 text-gray-900">{t.section1.card4.title}</h4>
                                    <p className="text-sm text-gray-600 leading-relaxed font-bold">{t.section1.card4.desc}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 order-1 lg:order-2">
                            <h2 className="text-indigo-600 font-black mb-4 uppercase tracking-widest text-sm">{t.section1.title}</h2>
                            <h3 className="text-4xl lg:text-5xl font-black mb-8 leading-tight">{t.section1.heading}</h3>
                            <p className="text-xl text-gray-600 leading-relaxed mb-8">
                                {t.section1.desc}
                            </p>
                            <ul className="space-y-4">
                                {t.section1.features.map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 font-bold text-gray-700">
                                        <CheckCircle2 className="text-green-500" size={20} /> {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- SECTION 2: SERIAL & APPOINTMENT (THE STRESS KILLER) --- */}
            <section id="appointment" className="py-24 bg-indigo-50 border-y border-indigo-100 overflow-hidden relative">
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-indigo-600 font-black mb-4 uppercase tracking-widest text-sm">{t.section2.title}</h2>
                        <h3 className="text-4xl lg:text-5xl font-black mb-6">{t.section2.heading}</h3>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            {t.section2.desc}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {/* Appointment Card */}
                        <div className="bg-white p-8 rounded-3xl shadow-xl shadow-indigo-100 border border-indigo-50">
                            <Calendar className="text-indigo-600 mb-6" size={40} />
                            <h4 className="text-2xl font-bold mb-4">{t.section2.card1.title}</h4>
                            <p className="text-gray-600 leading-relaxed mb-6">{t.section2.card1.desc}</p>
                            <div className="p-4 bg-indigo-50 rounded-xl text-indigo-700 font-bold text-sm">{t.section2.card1.badge}</div>
                        </div>

                        {/* Staff Role Card */}
                        <div className="bg-white p-8 rounded-3xl shadow-xl shadow-indigo-100 border border-indigo-50">
                            <Users className="text-green-600 mb-6" size={40} />
                            <h4 className="text-2xl font-bold mb-4">{t.section2.card2.title}</h4>
                            <p className="text-gray-600 leading-relaxed mb-6">{t.section2.card2.desc}</p>
                            <div className="p-4 bg-green-50 rounded-xl text-green-700 font-bold text-sm">{t.section2.card2.badge}</div>
                        </div>

                        {/* Global Reception Card */}
                        <div className="bg-white p-8 rounded-3xl shadow-xl shadow-indigo-100 border border-indigo-50">
                            <Monitor className="text-blue-600 mb-6" size={40} />
                            <h4 className="text-2xl font-bold mb-4">{t.section2.card3.title}</h4>
                            <p className="text-gray-600 leading-relaxed mb-6">{t.section2.card3.desc}</p>
                            <div className="p-4 bg-blue-50 rounded-xl text-blue-700 font-bold text-sm">{t.section2.card3.badge}</div>
                        </div>

                        {/* Offline Booking Card */}
                        <div className="bg-white p-8 rounded-3xl shadow-xl shadow-indigo-100 border border-indigo-50">
                            <Smartphone className="text-purple-600 mb-6" size={40} />
                            <h4 className="text-2xl font-bold mb-4">{t.section2.card4.title}</h4>
                            <p className="text-gray-600 leading-relaxed mb-6">{t.section2.card4.desc}</p>
                            <div className="p-4 bg-purple-50 rounded-xl text-purple-700 font-bold text-sm">{t.section2.card4.badge}</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- SECTION 3: DOCTOR WEBSITE (THE PROFESSIONAL BRAND) --- */}
            <section id="website" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col lg:flex-row items-center gap-16">
                        <div className="flex-1">
                            <h2 className="text-indigo-600 font-black mb-4 uppercase tracking-widest text-sm">{t.section3.title}</h2>
                            <h3 className="text-4xl lg:text-5xl font-black mb-8 leading-tight">{t.section3.heading}</h3>
                            <p className="text-xl text-gray-600 leading-relaxed mb-8">
                                {t.section3.desc}
                            </p>
                            <div className="space-y-6">
                                <div className="flex gap-4 p-4 hover:bg-gray-50 rounded-2xl transition group">
                                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition"><Search /></div>
                                    <div>
                                        <h5 className="font-bold text-lg">{t.section3.feature1.title}</h5>
                                        <p className="text-gray-500 text-sm">{t.section3.feature1.desc}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 p-4 hover:bg-gray-50 rounded-2xl transition group">
                                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition"><Globe /></div>
                                    <div>
                                        <h5 className="font-bold text-lg">{t.section3.feature2.title}</h5>
                                        <p className="text-gray-500 text-sm">{t.section3.feature2.desc}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 bg-gray-900 rounded-[3rem] p-8 shadow-2xl relative overflow-hidden">
                             <div className="absolute top-0 right-0 p-8 opacity-20"><Globe size={200} className="text-white" /></div>
                             <div className="relative z-10 text-white">
                                 <div className="w-24 h-24 bg-indigo-500 rounded-2xl mb-6 shadow-xl border-4 border-indigo-400"></div>
                                 <h4 className="text-3xl font-black mb-2">Dr. Anisur Rahman</h4>
                                 <p className="text-indigo-300 font-bold mb-8">MBBS, FCPS (Medicine) | Specialist Physician</p>
                                 <div className="space-y-3 mb-12">
                                     <div className="flex items-center gap-3 text-sm bg-white/5 p-3 rounded-xl border border-white/10"><Check className="text-indigo-400" /> Chambers: Dhanmondi & Uttara</div>
                                     <div className="flex items-center gap-3 text-sm bg-white/5 p-3 rounded-xl border border-white/10"><Check className="text-indigo-400" /> Availability: Sat - Thu (5pm - 9pm)</div>
                                 </div>
                                 <button className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-500 transition shadow-lg shadow-indigo-900/40">Book Appointment</button>
                             </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- SECTION 4: ALL FEATURES GRID (THE ECOSYSTEM) --- */}
            <section id="features" className="py-24 bg-linear-to-b from-gray-50 to-white border-y border-gray-100">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl lg:text-6xl font-black mb-6">{t.section4.title}</h2>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed font-bold">{t.section4.subtitle}</p>
                        <div className="mt-8 flex items-center justify-center gap-2">
                            <div className="h-1 w-12 bg-indigo-600 rounded-full"></div>
                            <p className="text-sm text-indigo-600 font-black uppercase tracking-wider">DrChamber প্রিমিয়াম সুবিধা</p>
                            <div className="h-1 w-12 bg-indigo-600 rounded-full"></div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {t.section4.features.map((feature, idx) => {
                            const iconMap = {
                                'brain': <Brain />,
                                'database': <Database />,
                                'calendar': <Calendar />,
                                'qrcode': <QrCode />,
                                'users': <Users />,
                                'monitor': <Monitor />,
                                'shield': <ShieldCheck />,
                                'wallet': <Wallet />,
                                'zap': <Zap />,
                                'activity': <Activity />,
                                'filetext': <FileText />,
                                'globe': <Globe />,
                                'smartphone': <Smartphone />,
                                'plus': <CheckCircle2 />,
                                'check': <Check />,
                                'languages': <Languages />
                            };
                            return (
                                <div key={idx} className="group h-full p-6 bg-white rounded-2xl border-2 border-gray-100 hover:border-indigo-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                                    <div className="flex flex-col h-full">
                                        <div className="w-14 h-14 bg-linear-to-br from-indigo-50 to-blue-50 rounded-xl flex items-center justify-center text-indigo-600 mb-4 group-hover:from-indigo-600 group-hover:to-blue-600 group-hover:text-white transition-all duration-300 shadow-md">
                                            {iconMap[feature.icon]}
                                        </div>
                                        <h5 className="font-black text-base mb-3 text-gray-900 leading-snug group-hover:text-indigo-600 transition">{feature.title}</h5>
                                        <p className="text-sm text-gray-600 leading-relaxed font-bold grow">{feature.desc}</p>
                                        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
                                            <div className="w-2 h-2 bg-indigo-600 rounded-full group-hover:scale-150 transition"></div>
                                            <span className="text-xs text-indigo-600 font-black uppercase tracking-wider">Premium</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Bottom CTA Section */}
                    <div className="mt-20 p-10 lg:p-16 rounded-4xl bg-linear-to-r from-indigo-600 to-blue-600 text-white text-center relative overflow-hidden">
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -mr-32 -mt-32"></div>
                        </div>
                        <div className="relative z-10">
                            <h3 className="text-3xl lg:text-4xl font-black mb-4">সব কিছু এক প্ল্যাটফর্মে</h3>
                            <p className="text-lg lg:text-xl text-indigo-100 mb-8 max-w-2xl mx-auto font-bold">আপনার চেম্বারের সম্পূর্ণ ডিজিটাল রূপান্তরের জন্য প্রয়োজনীয় সবকিছু এখানে রয়েছে। আর অন্যকোনো সফটওয়্যার লাগবে না!</p>
                            <Link to="/register" className="inline-flex items-center gap-3 px-10 py-4 bg-white text-indigo-600 font-black rounded-2xl hover:bg-gray-100 transition transform hover:scale-105 shadow-xl">
                                সম্পূর্ণ অ্যাক্সেস পান <ArrowRight size={24} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- THE "LIFE WITHOUT VS LIFE WITH" SECTION --- */}
            <section className="py-24 bg-indigo-900 text-white overflow-hidden relative">
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="text-center mb-20">
                        <h3 className="text-4xl lg:text-6xl font-black leading-tight">{language === 'bn' ? 'DrChamber কেন আপনার চেম্বারের অবিচ্ছেদ্য অংশ?' : 'Why is DrChamber essential for your chamber?'}</h3>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        <div className="p-10 bg-white/5 rounded-[3rem] border border-white/10 backdrop-blur-sm relative">
                            <div className="absolute top-0 right-0 bg-red-500 text-white px-6 py-2 rounded-bl-3xl font-bold uppercase text-xs tracking-widest">{t.comparison.without.title}</div>
                            <ul className="space-y-6 opacity-60">
                                {t.comparison.without.items.map((item, i) => (
                                    <li key={i} className="flex items-start gap-4 text-lg">❌ {item}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="p-10 bg-linear-to-br from-indigo-500 to-indigo-700 rounded-[3rem] border border-indigo-400 shadow-2xl relative">
                            <div className="absolute top-0 right-0 bg-green-500 text-white px-6 py-2 rounded-bl-3xl font-bold uppercase text-xs tracking-widest">{t.comparison.with.title}</div>
                            <ul className="space-y-6">
                                {t.comparison.with.items.map((item, i) => (
                                    <li key={i} className="flex items-start gap-4 text-lg font-bold">✅ {item}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- HOW IT WORKS SECTION --- */}
            <section className="py-24 bg-linear-to-b from-white to-gray-50">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-20">
                        <h3 className="text-4xl lg:text-6xl font-black mb-6">{t.howItWorks.title}</h3>
                        <p className="text-xl text-gray-600 font-bold max-w-2xl mx-auto">{t.howItWorks.subtitle}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
                        {t.howItWorks.steps.map((step, idx) => (
                            <div key={idx} className="relative">
                                {/* Connector Line */}
                                {idx < t.howItWorks.steps.length - 1 && (
                                    <div className="absolute top-20 left-[60%] w-[40vw] md:w-full h-1 bg-linear-to-r from-indigo-600 to-transparent -z-10 hidden md:block"></div>
                                )}
                                <div className="p-8 bg-white rounded-3xl border-2 border-indigo-200 text-center hover:shadow-xl transition transform hover:-translate-y-2">
                                    <div className="w-20 h-20 bg-linear-to-br from-indigo-600 to-blue-600 text-white text-4xl font-black rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                                        {step.number}
                                    </div>
                                    <h4 className="text-xl font-black text-gray-900 mb-3">{step.title}</h4>
                                    <p className="text-gray-600 font-bold text-sm leading-relaxed">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-16 p-10 lg:p-16 rounded-4xl bg-linear-to-r from-blue-600 to-indigo-600 text-white text-center">
                        <h3 className="text-3xl lg:text-4xl font-black mb-4">{language === 'bn' ? 'মাত্র ২ মিনিটে শুরু করুন' : 'Get Started in Just 2 Minutes'}</h3>
                        <p className="text-lg text-blue-100 mb-8 font-bold">{language === 'bn' ? 'ক্রেডিট কার্ড ছাড়াই সম্পূর্ণ ফিচার অ্যাক্সেস করুন।' : 'No credit card needed. Cancel anytime.'}</p>
                        <Link to="/register" className="inline-flex items-center gap-3 px-10 py-4 bg-white text-indigo-600 font-black rounded-2xl hover:bg-gray-100 transition transform hover:scale-105">
                            {language === 'bn' ? 'এখনই শুরু করুন' : 'Start Now'} <ArrowRight size={24} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* --- TESTIMONIALS SECTION --- */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-20">
                        <h3 className="text-4xl lg:text-6xl font-black mb-6">{t.testimonials.title}</h3>
                        <p className="text-xl text-gray-600 font-bold max-w-2xl mx-auto">{t.testimonials.subtitle}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {t.testimonials.testimonials.map((testimonial, idx) => (
                            <div key={idx} className="p-8 bg-linear-to-br from-indigo-50 to-blue-50 rounded-2xl border-2 border-indigo-100 hover:shadow-xl transition transform hover:-translate-y-2">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="text-4xl">{testimonial.image}</div>
                                    <div className="grow">
                                        <h5 className="font-black text-gray-900 text-sm leading-tight">{testimonial.name}</h5>
                                        <p className="text-xs text-gray-600 font-bold">{testimonial.title}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <span key={i} className="text-yellow-400 text-lg">⭐</span>
                                    ))}
                                </div>
                                <p className="text-gray-700 font-bold text-sm leading-relaxed italic">"{testimonial.text}"</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- FAQ SECTION --- */}
            <section className="py-24 bg-linear-to-b from-gray-50 to-white">
                <div className="max-w-3xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h3 className="text-4xl lg:text-5xl font-black mb-4">{t.faq.title}</h3>
                        <p className="text-lg text-gray-600 font-bold">{t.faq.subtitle}</p>
                    </div>
                    <div className="space-y-4">
                        {t.faq.questions.map((item, idx) => (
                            <details key={idx} className="group p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-indigo-300 hover:shadow-lg transition cursor-pointer">
                                <summary className="flex items-start justify-between font-black text-gray-900 text-lg cursor-pointer select-none group-open:text-indigo-600 transition">
                                    <span className="grow text-left">{item.q}</span>
                                    <span className="ml-4 text-2xl text-gray-400 group-open:text-indigo-600 transition">+</span>
                                </summary>
                                <p className="mt-4 text-gray-700 font-bold leading-relaxed text-base">{item.a}</p>
                            </details>
                        ))}
                    </div>
                    <div className="mt-16 p-10 bg-linear-to-r from-indigo-600 to-blue-600 text-white rounded-3xl text-center">
                        <h3 className="text-2xl font-black mb-3">{language === 'bn' ? 'আরও প্রশ্ন?' : 'Still have questions?'}</h3>
                        <p className="text-indigo-100 mb-6 font-bold">{language === 'bn' ? 'আমাদের সাপোর্ট টিম ২৪/৭ আপনার সেবায় প্রস্তুত।' : 'Our support team is available 24/7.'}</p>
                        <a href="mailto:support@DrChamber.com" className="inline-flex items-center gap-2 px-8 py-3 bg-white text-indigo-600 font-black rounded-xl hover:bg-gray-100 transition">
                            {language === 'bn' ? 'আমাদের সাথে যোগাযোগ করুন' : 'Contact Us'}
                        </a>
                    </div>
                </div>
            </section>

            {/* --- PRICING & BKASH --- */}
            <section id="pricing" className="py-24 bg-linear-to-b from-white to-indigo-50">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <div className="mb-4">
                        <h3 className="text-4xl lg:text-5xl font-black mb-4">{t.pricing.title}</h3>
                        <p className="text-xl text-gray-600 font-bold">{language === 'bn' ? 'কোনো লুকানো খরচ নেই। সব সময় বাতিল করুন।' : 'No hidden fees. Cancel anytime.'}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mt-16">
                        {/* FREE TRIAL CARD */}
                        <div className="p-8 rounded-[2.5rem] bg-white border-2 border-gray-200 flex flex-col items-center hover:shadow-xl transition transform hover:-translate-y-2">
                            <div className="bg-green-50 text-green-700 px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider mb-6">{t.pricing.free.badge}</div>
                            <h4 className="text-2xl font-black mb-2 text-gray-900">{t.pricing.free.title}</h4>
                            <div className="text-5xl font-black text-green-600 mb-1">{t.pricing.free.price}</div>
                            <p className="text-gray-500 mb-8 font-bold">{t.pricing.free.subtitle}</p>
                            <ul className="text-left space-y-4 mb-10 text-gray-600 font-bold w-full">
                                {t.pricing.free.features.map((feat, i) => (
                                    <li key={i} className="flex items-center gap-3"><Check className="text-green-500" size={20} /> <span>{feat}</span></li>
                                ))}
                            </ul>
                            <Link to="/register" className="w-full py-4 bg-green-600 text-white font-black rounded-2xl hover:bg-green-700 transition">{t.pricing.free.cta}</Link>
                        </div>

                        {/* MONTHLY CARD */}
                        <div className="p-8 rounded-[2.5rem] bg-white border-2 border-indigo-200 flex flex-col items-center hover:shadow-xl transition transform hover:-translate-y-2">
                            <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider mb-6">{language === 'bn' ? 'জনপ্রিয় পছন্দ' : 'Popular Choice'}</div>
                            <h4 className="text-2xl font-black mb-2 text-gray-900">{t.pricing.monthly.title}</h4>
                            <div className="text-5xl font-black text-indigo-600 mb-1">{t.pricing.monthly.price}</div>
                            <p className="text-gray-500 mb-2 font-bold">{t.pricing.monthly.subtitle}</p>
                            <p className="text-green-600 text-sm font-bold mb-8">{language === 'bn' ? '৳ 500/মাস বাঁচান' : 'Save ৳500/month'}</p>
                            <ul className="text-left space-y-4 mb-10 text-gray-600 font-bold w-full">
                                {t.pricing.monthly.features.map((feat, i) => (
                                    <li key={i} className="flex items-center gap-3"><Check className="text-indigo-500" size={20} /> <span>{feat}</span></li>
                                ))}
                            </ul>
                            <Link to="/register" className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition">{t.pricing.monthly.cta}</Link>
                        </div>

                        {/* YEARLY CARD - FEATURED */}
                        <div className="p-8 rounded-[2.5rem] bg-linear-to-br from-indigo-900 to-indigo-700 text-white flex flex-col items-center relative overflow-hidden border-4 border-indigo-500 shadow-2xl transform md:scale-105 hover:shadow-2xl transition">
                            <div className="absolute top-0 right-0 bg-pink-500 text-white px-4 py-2 rounded-bl-3xl font-black uppercase text-xs tracking-wider">{t.pricing.yearly.badge}</div>
                            <div className="absolute top-0 left-0 bg-yellow-400 text-gray-900 px-4 py-2 rounded-br-3xl font-black uppercase text-xs tracking-wider">{language === 'bn' ? 'সেরা মূল্য' : 'Best Value'}</div>
                            <h4 className="text-2xl font-black mb-2 mt-6">{t.pricing.yearly.title}</h4>
                            <div className="text-5xl font-black text-yellow-300 mb-1">{t.pricing.yearly.price}</div>
                            <p className="text-indigo-100 mb-2 font-bold">{t.pricing.yearly.subtitle}</p>
                            <p className="text-yellow-300 text-sm font-bold mb-8">{language === 'bn' ? 'প্রতি মাসে মাত্র ৳ 825 ' : 'Only ৳825/month'}</p>
                            <ul className="text-left space-y-4 mb-10 text-indigo-100 font-bold w-full">
                                {t.pricing.yearly.features.map((feat, i) => (
                                    <li key={i} className="flex items-center gap-3"><Check className="text-yellow-300" size={20} /> <span>{feat}</span></li>
                                ))}
                            </ul>
                            <Link to="/register" className="w-full py-4 bg-yellow-300 text-indigo-900 font-black rounded-2xl hover:bg-yellow-200 transition">{t.pricing.yearly.cta}</Link>
                        </div>
                    </div>

                    {/* Payment Methods */}
                    {/* <div className="mt-20 pt-12 border-t border-gray-200">
                        <p className="text-gray-600 font-bold mb-6">{language === 'bn' ? 'সুরক্ষিত পেমেন্ট গেটওয়েতে দ্বারা সমর্থিত:' : 'Supported by trusted payment gateways:'}</p>
                        <div className="flex flex-wrap justify-center gap-8 items-center">
                            <img src="https://upload.wikimedia.org/wikipedia/commons/8/8c/BKash_Logo.svg" alt="bKash" className="h-10" />
                            <span className="text-2xl font-black text-gray-400">NAGAD</span>
                            <span className="text-2xl font-black text-gray-400">VISA/MASTERCARD</span>
                        </div>
                    </div> */}
                </div>
            </section>

            {/* --- FINAL CTA --- */}
            <section className="py-24 bg-linear-to-b from-white to-indigo-50">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-4xl lg:text-6xl font-black mb-10">{t.cta.title}</h2>
                    <p className="text-xl text-gray-600 mb-12">{t.cta.subtitle}</p>
                    <Link to="/register" className="inline-flex items-center gap-4 px-12 py-6 bg-indigo-600 text-white text-2xl font-black rounded-4xl shadow-2xl shadow-indigo-300 hover:bg-indigo-700 transition transform hover:scale-105">
                        {t.cta.button} <ArrowRight size={32} />
                    </Link>
                </div>
            </section>

            {/* --- FOOTER --- */}
            <footer className="bg-white border-t border-gray-100 pt-20 pb-10">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-12 mb-16 font-bold">
                        <div className="col-span-2 lg:col-span-2">
                            <div className="flex items-center gap-2 mb-8 font-bold">
                                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center font-bold">
                                    <HeartPulse className="text-white w-6 h-6" />
                                </div>
                                <span className="font-black text-2xl tracking-tighter text-indigo-900">DrChamber</span>
                            </div>
                            <p className="max-w-xs text-gray-500 text-lg leading-relaxed font-bold">
                                {t.footer.desc}
                            </p>
                        </div>
                        <div>
                            <h5 className="font-black text-gray-900 mb-6">{t.footer.sections.chamber}</h5>
                            <ul className="space-y-4 text-sm font-bold text-gray-500">
                                <li><Link to="/staff/login" className="hover:text-indigo-600">{t.footer.links.staffPanel}</Link></li>
                                <li><Link to="/global/login" className="hover:text-indigo-600">{t.footer.links.receptionLogin}</Link></li>
                                <li><Link to="/admin/login" className="hover:text-indigo-600">{t.footer.links.superAdmin}</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h5 className="font-black text-gray-900 mb-6">{t.footer.sections.patients}</h5>
                            <ul className="space-y-4 text-sm font-bold text-gray-500">
                                <li><Link to="/find-doctors" className="hover:text-indigo-600">{t.footer.links.findDoctors}</Link></li>
                                <li><Link to="/patient/login" className="hover:text-indigo-600">{t.footer.links.downloadRx}</Link></li>
                                <li><Link to="/patient/register" className="hover:text-indigo-600">{t.footer.links.newAccount}</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h5 className="font-black text-gray-900 mb-6">{t.footer.sections.partnership}</h5>
                            <ul className="space-y-4 text-sm font-bold text-gray-500">
                                <li><Link to="/partners" className="hover:text-indigo-600">{t.footer.links.partnerProgram}</Link></li>
                                <li><Link to="/affiliate/login" className="hover:text-indigo-600">{t.footer.links.affiliate}</Link></li>
                                <li><Link to="/affiliate/register" className="hover:text-indigo-600">{t.footer.links.joinReferrer}</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="pt-8 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4 text-gray-400 text-sm font-bold">
                        <p>
                            <a href="https://thewebpal.com/" target="_blank" className="hover:text-indigo-600">{t.footer.copyright.replace('{year}', new Date().getFullYear())}</a> |
                        </p>
                        <div className="flex gap-6 font-bold">
                             <span>Privacy</span>
                             <span>Terms</span>
                             <span className="text-green-500">{t.footer.status}</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default Home;
